import { DateTime } from "luxon";
import { useMemo } from "react";
import type { CaptureBucket, CaptureDateStats } from "../capture-date-stats";
import type { CalendarBounds } from "./capture-calendar-types";

/**
 * @returns A pluralized capture count, for tooltips
 * @param count How many captures the cell holds
 */
export function captureLabel(count: number): string {
  return `${count} ${count === 1 ? "capture" : "captures"}`;
}

/**
 * @returns The span the user may navigate and select within: from the month of the first
 * capture up to today, so no screen is empty of captures or entirely in the future
 * @param stats The project capture histogram
 */
export function useSelectableBounds(stats: CaptureDateStats): CalendarBounds {
  return useMemo(() => {
    const now = DateTime.now();
    const first = stats.days.length
      ? DateTime.fromJSDate(stats.days[0].start)
      : now;
    return { min: first.startOf("month"), max: now.endOf("day") };
  }, [stats]);
}

/**
 * @returns A lookup from bucket start (in millis) to capture count
 * @param buckets The buckets to index
 */
export function useBucketCounts(buckets: CaptureBucket[]): Map<number, number> {
  return useMemo(
    () => new Map(buckets.map(({ start, count }) => [start.getTime(), count])),
    [buckets],
  );
}
